import { useState } from 'react';
import { submitContact } from '../lib/db';
import { Mail, Phone, MapPin, Send, Loader2, CheckCircle2, AlertCircle, Globe, Instagram, Linkedin, MessageCircle } from 'lucide-react';
import { useSeo } from '../lib/seo';
import { useSiteSettings } from '../lib/site-settings';

export default function Contact() {
  useSeo({
    title: 'Contact Kisapuk Community Initiative',
    description: 'Get in touch with Kisapuk Community Initiative to learn how to support our programs, volunteer, or collaborate.',
    pathname: '/contact',
    image: 'https://your-domain.com/contact-social.png',
  });

  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const { settings } = useSiteSettings();

  function validate(): boolean {
    const e: Record<string, string> = {};
    if (!form.name.trim()) e.name = 'Name is required';
    else if (form.name.trim().length < 2) e.name = 'Name must be at least 2 characters';
    if (!form.email.trim()) e.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Please enter a valid email';
    if (!form.subject.trim()) e.subject = 'Subject is required';
    if (!form.message.trim()) e.message = 'Message is required';
    else if (form.message.trim().length < 10) e.message = 'Message must be at least 10 characters';
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    setStatus('loading');
    setErrorMsg('');
    try {
      await submitContact(form);
      setStatus('success');
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch (err) {
      setStatus('error');
      setErrorMsg(err instanceof Error ? err.message : 'Something went wrong');
    }
  }

  const contactInfo = [
    { icon: Mail, label: 'Email', value: settings.contact_email || 'info@kisapuk.org', href: `mailto:${settings.contact_email || 'info@kisapuk.org'}` },
    { icon: Phone, label: 'Phone', value: settings.contact_phone || '+254 700 000 000', href: `tel:${settings.contact_phone || '+254700000000'}` },
    { icon: MapPin, label: 'Address', value: settings.contact_address || 'Kisapuk, Rift Valley, Kenya' },
  ];

  const socialLinks = [
    { icon: Globe, label: 'Facebook', href: settings.facebook_url || 'https://facebook.com', value: settings.facebook_url || 'facebook.com' },
    { icon: MessageCircle, label: 'WhatsApp', href: settings.whatsapp_number ? `https://wa.me/${settings.whatsapp_number.replace(/[^0-9]/g, '')}` : 'https://wa.me/254700000000', value: settings.whatsapp_number || '+254 700 000 000' },
    { icon: Instagram, label: 'Instagram', href: settings.instagram_url || 'https://instagram.com', value: settings.instagram_url || 'instagram.com' },
    { icon: Linkedin, label: 'LinkedIn', href: settings.linkedin_url || 'https://linkedin.com', value: settings.linkedin_url || 'linkedin.com' },
  ];

  return (
    <div>
      {/* Hero */}
      <section className="pt-32 pb-16 bg-brand-900">
        <div className="container-prose text-center">
          <span className="badge-gold mb-4">Get in Touch</span>
          <h1 className="font-display text-4xl font-bold text-white sm:text-5xl">
            Contact Us
          </h1>
          <p className="mt-4 text-lg text-brand-100 max-w-2xl mx-auto">
            Have a question, idea, or want to partner with us? We'd love to hear from you.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 lg:py-24">
        <div className="container-prose">
          <div className="grid gap-12 lg:grid-cols-2">
            {/* Info */}
            <div>
              <h2 className="font-display text-2xl font-bold text-stone-900 mb-6">
                Let's Start a Conversation
              </h2>
              <p className="text-stone-600 leading-relaxed mb-8">
                Whether you're interested in volunteering, partnering, donating, or just learning more about our work, our team is ready to help. Reach out using the form or through any of the channels below.
              </p>
              <div className="space-y-4">
                {contactInfo.map((info) => (
                  <div key={info.label} className="flex items-start gap-4 p-4 rounded-xl bg-stone-50 border border-stone-200">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-100 text-brand-600 flex-shrink-0">
                      <info.icon className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-stone-500">{info.label}</p>
                      {info.href ? (
                        <a href={info.href} className="text-stone-900 font-medium hover:text-brand-700 transition-colors">{info.value}</a>
                      ) : (
                        <p className="text-stone-900 font-medium">{info.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6">
                <h3 className="font-display text-xl font-semibold text-stone-900 mb-4">Follow and Connect</h3>
                <div className="grid gap-3 sm:grid-cols-2">
                  {socialLinks.map((link) => (
                    <a
                      key={link.label}
                      href={link.href}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-3 rounded-xl border border-stone-200 bg-white p-3 transition-colors hover:border-brand-300 hover:bg-brand-50"
                    >
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-100 text-brand-600">
                        <link.icon className="h-5 w-5" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-stone-900">{link.label}</p>
                        <p className="truncate text-sm text-stone-500">{link.value}</p>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="card p-6 lg:p-8">
              {status === 'success' ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100 text-green-600 mb-4">
                    <CheckCircle2 className="h-8 w-8" />
                  </div>
                  <h3 className="font-display text-xl font-bold text-stone-900">Message Sent!</h3>
                  <p className="mt-2 text-stone-600">Thank you for reaching out. We'll get back to you soon.</p>
                  <button onClick={() => setStatus('idle')} className="btn-outline mt-6">
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} noValidate className="space-y-5">
                  <div>
                    <label htmlFor="name" className="label">Name</label>
                    <input
                      id="name"
                      type="text"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="input"
                      placeholder="Your full name"
                      aria-invalid={!!errors.name}
                    />
                    {errors.name && <p className="mt-1 text-sm text-red-600 flex items-center gap-1"><AlertCircle className="h-3 w-3" />{errors.name}</p>}
                  </div>
                  <div>
                    <label htmlFor="email" className="label">Email</label>
                    <input
                      id="email"
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="input"
                      placeholder="you@example.com"
                      aria-invalid={!!errors.email}
                    />
                    {errors.email && <p className="mt-1 text-sm text-red-600 flex items-center gap-1"><AlertCircle className="h-3 w-3" />{errors.email}</p>}
                  </div>
                  <div>
                    <label htmlFor="subject" className="label">Subject</label>
                    <input
                      id="subject"
                      type="text"
                      value={form.subject}
                      onChange={(e) => setForm({ ...form, subject: e.target.value })}
                      className="input"
                      placeholder="What's this about?"
                      aria-invalid={!!errors.subject}
                    />
                    {errors.subject && <p className="mt-1 text-sm text-red-600 flex items-center gap-1"><AlertCircle className="h-3 w-3" />{errors.subject}</p>}
                  </div>
                  <div>
                    <label htmlFor="message" className="label">Message</label>
                    <textarea
                      id="message"
                      rows={5}
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      className="input resize-none"
                      placeholder="Tell us more..."
                      aria-invalid={!!errors.message}
                    />
                    {errors.message && <p className="mt-1 text-sm text-red-600 flex items-center gap-1"><AlertCircle className="h-3 w-3" />{errors.message}</p>}
                  </div>

                  {status === 'error' && (
                    <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700 flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 flex-shrink-0" />
                      {errorMsg}
                    </div>
                  )}

                  <button type="submit" disabled={status === 'loading'} className="btn-primary w-full">
                    {status === 'loading' ? (
                      <><Loader2 className="h-4 w-4 animate-spin" /> Sending...</>
                    ) : (
                      <><Send className="h-4 w-4" /> Send Message</>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
